import { DateTime } from 'luxon';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';

import { CELL_HEIGHT, FORMAT_DATE } from '../const';
import { checkAssigneeData, getStartOfWeek, getTimeFromNumberRow } from '../functions';
import { TaskTypeInput } from '../graphql/__generated__/UpdateTaskMutation.graphql';
import UpdateTaskMutation from '../graphql/mutations/UpdateTaskMutation';
import useCommitTask from '../hooks/useCommitTask';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterCalendarTasks } from '../store/slices/calendarTasksSlice';
import { DayWeekType, ItemTaskType, TaskPositionType, TaskType } from '../types';
import CalendarTaskBody from './calendarTaskBody';
import CalendarTaskBorder from './calendarTaskBorder';

interface ICalendarTask {
    typeDayWeek: DayWeekType;
    task: TaskPositionType;
    cellWidth: number;
}

const CalendarTask: React.FC<ICalendarTask> = ({ typeDayWeek, task, cellWidth }) => {
    const dispatch = useDispatch<ComponentsDispatch>();

    const currentDate = useSelector<RootState, string>((state) => state.calendar.currentDate);
    const isDragging = useSelector<RootState, boolean>((state) => state.calendar.isDragging);
    const listTask = useSelector<RootState, TaskType[]>((state) => state.calendar.listTask);
    const currentTask = useSelector<RootState, ItemTaskType | null>((state) => state.calendar.currentTask);
    const connectionTaskId = useSelector<RootState, string>((state) => state.calendar.connectionTaskId);
    const [commitUpdate] = useMutation(UpdateTaskMutation);
    const { getCommitTask } = useCommitTask(typeDayWeek);

    const startValueY = useRef<number>(0);
    const heightBeforeDrag = useRef<number>(0);
    const rowBeforeDrag = useRef<number>(0);

    const handleDrag = (e: any, data: any, taskId: string) => {
        dispatch(setterCalendar({ isDragging: true }));
        dispatch(
            setterCalendarTasks({
                wasChangedTask: true,
            }),
        );

        const newTasks = listTask.map((task): TaskType => {
            if (task.id === taskId) {
                const newRow = Math.round(data.y / CELL_HEIGHT);
                const newCol = typeDayWeek === 'week' ? Math.round(data.x / cellWidth) : 0;

                const startWeekDate = getStartOfWeek(DateTime.fromISO(currentDate));
                const newDateTask = startWeekDate.plus({ day: newCol });

                return {
                    ...task,
                    row: newRow,
                    col: newCol,
                    start_datetime: getTimeFromNumberRow(newRow),
                    end_datetime: getTimeFromNumberRow(newRow + task.height),
                    datetime: typeDayWeek === 'week' ? newDateTask.toFormat(FORMAT_DATE) : task.datetime,
                };
            }
            return task;
        });
        dispatch(setterCalendar({ listTask: newTasks }));
    };

    const handleDragResize = (e: any, data: any, taskId: string, direction: 'up' | 'down') => {
        dispatch(setterCalendar({ isDragging: true }));

        const newTasks = listTask.map((task) => {
            if (task.id === taskId) {
                const diff = data.y - startValueY.current;
                const newHeight = heightBeforeDrag.current + (direction === 'up' ? -diff : diff) / CELL_HEIGHT;
                const newRow = rowBeforeDrag.current + (direction === 'up' ? diff / CELL_HEIGHT : 0);

                if (newHeight >= 1) {
                    return {
                        ...task,
                        height: newHeight,
                        row: newRow,
                        start_datetime: getTimeFromNumberRow(newRow),
                        end_datetime: getTimeFromNumberRow(newRow + newHeight),
                    };
                }
            }
            return task;
        });
        dispatch(setterCalendar({ listTask: newTasks }));
    };

    const saveDragResult = (taskId: string) => {
        if (!isDragging) {
            return;
        }

        const [newTask] = listTask
            .filter((task) => task.id === taskId)
            .map((task) => {
                const { row: _, col: __, height: ___, ...newTask } = task;
                return newTask;
            });

        dispatch(
            setterCalendarTasks({
                wasChangedTask: false,
            }),
        );

        commitUpdate(
            getCommitTask(
                {
                    ...newTask,
                    assignee_for_task: checkAssigneeData(newTask.assignee_for_task).join(','),
                } as TaskTypeInput,
                connectionTaskId,
                taskId,
            ),
        );

        if (currentTask) {
            dispatch(
                setterCalendar({
                    currentTask: newTask,
                }),
            );
        }

        setTimeout(() => {
            dispatch(
                setterCalendar({
                    isDragging: false,
                }),
            );
        });
    };

    return (
        <>
            <CalendarTaskBody
                task={task}
                typeDayWeek={typeDayWeek}
                saveDragResult={saveDragResult}
                handleDrag={handleDrag}
                cellWidth={cellWidth}
            />
            <CalendarTaskBorder
                task={task}
                typeBorder={'up'}
                cellWidth={cellWidth}
                saveDragResult={saveDragResult}
                handleDragResize={handleDragResize}
                startValueY={startValueY}
                heightBeforeDrag={heightBeforeDrag}
                rowBeforeDrag={rowBeforeDrag}
            />
            <CalendarTaskBorder
                task={task}
                typeBorder={'down'}
                cellWidth={cellWidth}
                saveDragResult={saveDragResult}
                handleDragResize={handleDragResize}
                startValueY={startValueY}
                heightBeforeDrag={heightBeforeDrag}
                rowBeforeDrag={rowBeforeDrag}
            />
        </>
    );
};

export default CalendarTask;
